import { Inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';

/**
 * Reads/writes cookies scoped to the registrable domain the app shares with Keycloak (e.g. `.edara.io`
 * in prod, `.getedara.com` in dev/staging), so a single cookie bridges the Edara app and the
 * Keycloak-hosted login page. It is the shared mechanism behind cross-surface preferences that BOTH
 * surfaces must agree on — the UI language ([[SwitchLanguageService]]) and the light/dark theme
 * ([[AppService]]).
 *
 * When the app and Keycloak are NOT on a shared registrable domain (localhost, or an env where Keycloak
 * lives on a different registrable domain), it transparently falls back to a host-only cookie so nothing
 * breaks where sharing is impossible — the bridge is simply inert there, and a one-time console warning
 * makes that observable per environment.
 */
@Injectable({ providedIn: 'root' })
export class SharedCookieService {

  private static readonly DEFAULT_TTL_DAYS = 30;

  /** Leading-dot registrable domain to scope cookies to (e.g. `.edara.io`), or null for host-only. */
  private readonly domain: string | null = this.resolveSharedDomain();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private cookieService: CookieService
  ) { }

  /** Read a cookie value, or null when absent. */
  public get(name: string): string | null {
    return this.cookieService.check(name) ? this.cookieService.get(name) : null;
  }

  /**
   * Write a cookie on the shared registrable domain (so Keycloak's login page receives it), or host-only
   * when sharing is impossible. `SameSite=Lax` (same-site top-level navigations app<->login carry it),
   * `Secure` only over https so http local testing on a shared `*.test`/hosts-mapped domain still works.
   */
  public set(name: string, value: string, days = SharedCookieService.DEFAULT_TTL_DAYS): void {
    const options: {
      expires: number; path: string; domain?: string;
      secure: boolean; sameSite: 'Lax' | 'None' | 'Strict';
    } = {
      expires: days,
      path: '/',
      sameSite: 'Lax',
      secure: this.document.location.protocol === 'https:'
    };

    if (this.domain) {
      this.clearHostOnly(name);
      options.domain = this.domain;
    }

    this.cookieService.set(name, value, options);
  }

  /**
   * Delete a cookie from BOTH scopes — the shared-domain one and any host-only cookie of the same
   * name that could otherwise shadow it and appear to survive the deletion.
   */
  public remove(name: string): void {
    this.clearHostOnly(name);
    if (this.domain) {
      this.cookieService.delete(name, '/', this.domain);
    }
  }

  /**
   * Remove any pre-existing HOST-ONLY cookie of this name so it can't shadow the shared-domain one.
   * A legacy cookie written with no explicit path took the RFC 6265 §5.1.4 default-path = the directory
   * of the route it was set on (e.g. `/invoicing`), and a deeper-path cookie is sent first (§5.4), so a
   * single `delete(name,'/')` is insufficient — clear every ancestor path of the current route. `delete`
   * with no domain targets only the host-only cookie, leaving the shared-domain one intact.
   */
  private clearHostOnly(name: string): void {
    const segments = this.document.location.pathname.split('/').filter(Boolean);
    let path = '';
    this.cookieService.delete(name, '/');
    for (const segment of segments) {
      path += `/${segment}`;
      this.cookieService.delete(name, path);
    }
  }

  /**
   * The leading-dot registrable domain to scope cookies to when — and only when — the app and Keycloak
   * share it, so the cookie is genuinely readable by both. Returns null for localhost/IPs or any env
   * where Keycloak is on a different registrable domain (then callers keep a host-only cookie).
   */
  private resolveSharedDomain(): string | null {
    try {
      const appDomain = this.registrableDomain(this.document.location.hostname);
      const keycloakDomain = this.registrableDomain(new URL(environment.keycloakConfig.url).hostname);
      if (appDomain && keycloakDomain && appDomain === keycloakDomain) {
        return `.${appDomain}`;
      }
      console.warn(
        `[shared-cookie] cross-surface cookies DISABLED: app '${this.document.location.hostname}' and ` +
        `Keycloak '${environment.keycloakConfig.url}' are on different registrable domains.`);
    } catch {
      // Malformed Keycloak URL / unavailable location — fall back to host-only cookies.
    }
    return null;
  }

  /**
   * Best-effort eTLD+1 for the two-label domains this app runs under (edara.io / getedara.com).
   * Returns null for localhost, single-label hosts and IP addresses, which have no shareable parent.
   */
  private registrableDomain(hostname: string): string | null {
    if (!hostname || /^[\d.]+$/.test(hostname)) {
      return null;
    }
    const labels = hostname.toLowerCase().split('.');
    if (labels.length < 2) {
      return null;
    }
    return labels.slice(-2).join('.');
  }
}
